import * as React from 'react'
import { cn } from '../../lib/cn'
import { IDENTIFY_ACCENT, POSTAUTH_ACCENT } from './types'

export type ForkDirection = 'out' | 'in'

export interface ForkConnectorProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * `out` = one source on the left fanning to two destinations on the
   * right (used at `identity_lookup` -> 02A/02B).
   * `in` = two sources on the left converging to one destination on
   * the right (used at end of 02A/02B -> post-auth entry).
   */
  direction: ForkDirection
  /** Top-lane stroke color. Defaults to `--pe-accent-existing`. */
  topColor?: string
  /** Bottom-lane stroke color. Defaults to `--pe-accent-newuser`. */
  bottomColor?: string
  /**
   * Source/destination joint dot color. Defaults to
   * `--pe-accent-identify` for fork-out and `--pe-accent-postauth` for
   * fork-in.
   */
  jointColor?: string
  /** Width of the fork channel in pixels. Mockup default: 56. */
  width?: number
}

/**
 * SVG bezier fork connector. Stretches to the full height of its
 * parent flex row so two-lane rows always connect smoothly. Stroke
 * width is fixed at 1.5px per mockup; bezier control points are
 * symmetrical so the curves visually meet at the lane mid-line.
 *
 * Source of truth: `mockups/unified-pipeline.html` `.connector.fork`.
 */
export const ForkConnector = React.forwardRef<HTMLDivElement, ForkConnectorProps>(
  function ForkConnector(
    {
      className,
      direction,
      topColor = 'var(--pe-accent-existing)',
      bottomColor = 'var(--pe-accent-newuser)',
      jointColor,
      width = 56,
      style,
      ...rest
    },
    ref,
  ) {
    const resolvedJoint = jointColor ?? (direction === 'out' ? IDENTIFY_ACCENT : POSTAUTH_ACCENT)

    // Bezier paths — mockup-exact:
    //   fork-out: source at (0,50) bends to (56,25) and (56,75)
    //   fork-in:  sources at (0,25) and (0,75) converge to (56,50)
    const paths =
      direction === 'out'
        ? [
            { d: 'M 0 50 C 28 50, 28 25, 56 25', stroke: topColor, label: 'top' },
            { d: 'M 0 50 C 28 50, 28 75, 56 75', stroke: bottomColor, label: 'bottom' },
          ]
        : [
            { d: 'M 0 25 C 28 25, 28 50, 56 50', stroke: topColor, label: 'top' },
            { d: 'M 0 75 C 28 75, 28 50, 56 50', stroke: bottomColor, label: 'bottom' },
          ]

    const jointCx = direction === 'out' ? 0 : 56

    return (
      <div
        ref={ref}
        className={cn('relative h-full shrink-0', className)}
        style={{ width, minWidth: width, ...style }}
        data-testid="pipeline-fork-connector"
        data-direction={direction}
        {...rest}
      >
        <svg
          viewBox="0 0 56 100"
          preserveAspectRatio="none"
          className="absolute inset-0 h-full w-full overflow-visible"
          aria-hidden="true"
        >
          {paths.map((p) => (
            <path
              key={p.label}
              d={p.d}
              stroke={p.stroke}
              strokeWidth={2}
              fill="none"
              // The fork SVG stretches to the full branch-row height
              // (preserveAspectRatio="none"). A scaled stroke collapses the
              // near-vertical bezier segments to a faint hairline at tall
              // heights; pinning the stroke keeps both fork arms prominent
              // and the green/amber lanes legible.
              vectorEffect="non-scaling-stroke"
              opacity={0.9}
            />
          ))}
          <circle
            cx={jointCx}
            cy={50}
            r={3}
            fill={resolvedJoint}
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>
    )
  },
)
